import React from "react";
import { useEffect, useMemo } from "react";
import { ClipLoaderCommon } from "../Common";
import { PromoTourModule } from "@module";
import {
    fetchHomePageSchema,
    fetchHomePageProductWithType,
    selectHomePageProduct,
    useAppDispatch,
    useAppSelector,
    setChipType,
    fetchFavoriteTours,
} from "@redux";
import styled from "styled-components";
import _ from "lodash";
import { breakpoint } from "@utilities";
import { DiscountBanner } from "@components";

export const ToursCategory = () => {
    const dispatch = useAppDispatch();
    const { homePageProducts, homePageSchema, status } = useAppSelector(selectHomePageProduct);
    const selectedChipType = useAppSelector((state) => state.homePageProduct.selectedChipType);
    const selectedLocations = useAppSelector((state) => state.locationSearch.selectedLocations);
    const authJWT = useAppSelector((state) => state.auth.jwt);
    const account = useAppSelector((state) => state.account.account);
    const activeDiscount = useAppSelector((state) => state.checkout.activeDiscount);

    useEffect(() => {
        const shouldNotFetch = !_.isNil(authJWT) && !_.isNil(account);
        if (!shouldNotFetch) return;
        dispatch(fetchFavoriteTours());
    }, [dispatch, authJWT, account]);

    useEffect(() => {
        dispatch(fetchHomePageSchema());
    }, [dispatch]);

    const chipTypes = useMemo(
        () => _.get(homePageSchema, "data", []).map((schema) => schema.type),
        [homePageSchema],
    );

    useEffect(() => {
        if (!_.isEmpty(chipTypes)) {
            const initialTourType = _.first(chipTypes);
            dispatch(setChipType(initialTourType!));
        }
    }, [chipTypes]);

    // Handle chip selection
    const handleTourTypeSelect = (selectedType: string) => {
        dispatch(setChipType(selectedType));
    };

    // Fetch products when schema, chip, or locations change
    useEffect(() => {
        const hasRequiredData = homePageSchema && selectedChipType;
        if (!hasRequiredData) return;
        dispatch(fetchHomePageProductWithType());
    }, [dispatch, homePageSchema, selectedChipType, selectedLocations]);

    return (
        <>
            <ChipsContainer>
                {chipTypes.map((tourType: string) => (
                    <Chips
                        key={tourType}
                        $active={tourType === selectedChipType}
                        onClick={() => handleTourTypeSelect(tourType)}
                    >
                        {tourType}
                    </Chips>
                ))}
            </ChipsContainer>
            <div className="category">
                {status === "loading" ? (
                    <ClipLoaderCommon color="#31B4B9" size={70} margin={150} />
                ) : _.isEmpty(homePageProducts) ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        No {selectedChipType} available at the moment
                    </div>
                ) : (
                    _.entries(homePageProducts).map(([name, category], idx) => (
                        <React.Fragment key={`${name}-${idx}`}>
                            <CategoryHeaderRow>
                                <CategoryHeading>{name}</CategoryHeading>
                            </CategoryHeaderRow>
                            <PromoTourModule data={category.tours} />
                            {idx === 0 &&
                                selectedChipType.trim().toLowerCase() === "experiences" &&
                                activeDiscount &&
                                activeDiscount.discount > 0 && (
                                    <DiscountBanner
                                        activeDiscountPercent={activeDiscount.discount}
                                        learnMoreHref={"/about/discounts"}
                                    />
                                )}
                        </React.Fragment>
                    ))
                )}
            </div>
        </>
    );
};

const ChipsContainer = styled.div`
    display: flex;
    gap: 10px;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    overflow-x: auto;
    margin: 15px auto;
    max-width: 100%;
    padding: 0 16px;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
        display: none;
    }
    scrollbar-width: none;

    ${breakpoint.breakTablet`
        margin-top: 70px;
        justify-content: center; 
    `}
`;

const Chips = styled.div<{ $active?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 40px;
    background: ${(props) =>
        props.$active ? "linear-gradient(90deg, #436CB2 0%, #28B7C3 100%)" : "#fff"};
    padding: 10px 20px;
    cursor: pointer;
    font-weight: 500;
    color: ${(props) => (props.$active ? "#fff" : "#3C3C3C")};
    flex: 0 0 auto;
    border: ${(props) => (props.$active ? "0.5px solid #D6F0F1" : "1px solid #F2F2F2")};
    z-index: 1;
`;

const CategoryHeading = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    flex: 1;
    line-height: 1.4;
    ${breakpoint.breakMobileMedium`
            width: ${(props) => props.theme.widths.wide_700};
        `}

    ${breakpoint.breakTablet`
            font-size: 28px;   
        `}
    
    ${breakpoint.breakIpadPro`
            width: ${(props) => props.theme.widths.wide_850}; 
        `}
    
    ${breakpoint.breakLarge`
            width: ${(props) => props.theme.widths.wide_1110};
        `}
`;

const CategoryHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 4px;
    margin: 0 auto 24px;
    flex-wrap: wrap;
    row-gap: 12px;

    ${breakpoint.breakTablet`
        width: ${(props) => props.theme.widths.wide_700};
    `}
    ${breakpoint.breakIpadPro`
        width: ${(props) => props.theme.widths.wide_850};
    `}
    ${breakpoint.breakLarge`
        width: ${(props) => props.theme.widths.wide_1110};
    `}
    ${breakpoint.breakMobile`
        flex-wrap: wrap;
        row-gap: 12px;
        flex-direction: row;
        align-items: flex-start;
        padding-left: 12px;
    `}
`;


